/**
 * Promo Bar Updater
 * Listens for cart update events and refreshes promo bar sections
 * to show live progress without page reload
 */

import { ThemeEvents } from '@theme/events';
const PROMO_BAR_UPDATER_STATE_KEY = '__mmPromoBarUpdaterState';
const promoBarUpdaterState = (window[PROMO_BAR_UPDATER_STATE_KEY] = window[PROMO_BAR_UPDATER_STATE_KEY] || {
  initialized: false,
  scheduledRefreshTimer: null,
});

/**
 * Fetches fresh HTML for a section and replaces it in the DOM
 * @param {Element} element - The element to replace
 * @param {string} dataAttribute - The data attribute to find the element
 */
async function refreshPromoBarSection(element, dataAttribute) {
  if (!element) return;

  // Find the section ID from the closest .shopify-section
  const sectionEl = element.closest('.shopify-section');
  if (!sectionEl) return;

  const sectionId = sectionEl.id?.replace('shopify-section-', '');
  if (!sectionId) return;

  try {
    // Fetch fresh section HTML
    const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
    if (!response.ok) return;

    const html = await response.text();
    
    // Parse the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Find the new content by the same data attribute
    const newContent = doc.querySelector(`[${dataAttribute}]`);
    
    if (newContent) {
      // Replace the old element with the new one
      element.replaceWith(newContent);
    }
  } catch (error) {
    console.error('Error refreshing promo bar:', error);
  }
}

/**
 * Refresh all promo bars after cart updates
 */
async function refreshPromoBars() {
  // Refresh announcement bar promo
  const announcementPromo = document.querySelector('[data-announcement-promo]');
  if (announcementPromo) {
    await refreshPromoBarSection(announcementPromo, 'data-announcement-promo');
  }

  // Refresh PDP promo bar
  const pdpPromo = document.querySelector('[data-pdp-promo-bar]');
  if (pdpPromo) {
    await refreshPromoBarSection(pdpPromo, 'data-pdp-promo-bar');
  }

  // Refresh quantity BXGY punch cards (header + PDP placements). The drawer
  // and cart-page instances refresh via their sections' own morphs.
  const qtyBar = document.querySelector('[data-qty-promo-bar]');
  if (qtyBar) {
    await refreshPromoBarSection(qtyBar, 'data-qty-promo-bar');
  }

  const qtyPdp = document.querySelector('[data-qty-promo-pdp]');
  if (qtyPdp) {
    await refreshPromoBarSection(qtyPdp, 'data-qty-promo-pdp');
  }
}

function schedulePromoBarRefresh() {
  if (promoBarUpdaterState.scheduledRefreshTimer) {
    clearTimeout(promoBarUpdaterState.scheduledRefreshTimer);
  }

  promoBarUpdaterState.scheduledRefreshTimer = window.setTimeout(() => {
    promoBarUpdaterState.scheduledRefreshTimer = null;
    refreshPromoBars();
  }, 0);
}

// Listen for cart update events
// Note: cart updates often morph sections; wait a tick so DOM is settled.
if (!promoBarUpdaterState.initialized) {
  document.addEventListener(
    ThemeEvents.cartUpdate,
    () => {
      schedulePromoBarRefresh();
    },
    { capture: true }
  );

  // Also listen for discount updates (they affect cart total)
  document.addEventListener(
    ThemeEvents.discountUpdate,
    () => {
      schedulePromoBarRefresh();
    },
    { capture: true }
  );

  promoBarUpdaterState.initialized = true;
}

// Export for testing
export { refreshPromoBars };
